import React, { useState, useEffect } from "react";
import { convertWeight } from "../../utils/weightUtils";
import Button from "../ui/Button";
import "./SendInvoiceModal.css";

function formatCurrency(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0.00";
    return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const SendInvoiceModal = ({ order, onSend, onCancel }) => {
    const [taxAmount, setTaxAmount] = useState("");
    const [notes, setNotes] = useState("");
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        const onKey = (e) => { 
            if (e.key === "Escape") onCancel();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onCancel]);

    const rawQuantity = parseFloat(order?.weight_requested_original) || 0;
    const buyerUnit = order?.weight_unit || order?.product_weight_unit || "g";
    const unitPrice = parseFloat(order?.listing_price) || 0;
    const priceUnit = order?.listing_price_unit || buyerUnit;

    const WEIGHT_UNITS = ["g", "oz", "lb"];
    const canConvert = WEIGHT_UNITS.includes(buyerUnit) && WEIGHT_UNITS.includes(priceUnit);
    const quantity = canConvert ? convertWeight(rawQuantity, buyerUnit, priceUnit) : rawQuantity;
    const weightUnit = canConvert ? priceUnit : buyerUnit;
    const lineTotal = parseFloat((quantity * unitPrice).toFixed(2));
    const tax = parseFloat(taxAmount) || 0;
    const total = lineTotal + tax;

    const handleSend = async () => {
        if (isSending) return;
        setIsSending(true);

        const payload = {
            line_items: [
                {
                    product_name: order.product_name || "Product",
                    description: null,
                    quantity,
                    weight_unit: weightUnit,
                    unit_price: unitPrice,
                },
            ],
            notes: notes.trim() || null,
            tax_amount: tax,
        };

        try {
            await onSend(payload);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="tw-modal-overlay" onClick={onCancel}>
            <div
                className="tw-modal send-invoice-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="send-invoice-title"
            >
                <div className="tw-modal__header">
                    <div>
                        <h3 className="tw-modal__title" id="send-invoice-title">
                            Send Invoice
                        </h3>
                        <p className="send-invoice-modal__subtitle">
                            to {order?.buyer_company || "Buyer"}
                        </p>
                    </div>
                    <button className="tw-modal__close" onClick={onCancel} aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="tw-modal__body send-invoice-modal__body">
                    {/* Order summary — auto-populated, read-only */}
                    <div className="send-invoice-modal__summary">
                        <div className="send-invoice-modal__summary-row">
                            <span className="send-invoice-modal__summary-label">Product</span>
                            <span className="send-invoice-modal__summary-value">
                                {order?.product_name || "—"}
                            </span>
                        </div>
                        <div className="send-invoice-modal__summary-row">
                            <span className="send-invoice-modal__summary-label">Quantity</span>
                            <span className="send-invoice-modal__summary-value">
                                {parseFloat(quantity.toFixed(4))} {weightUnit}
                            </span>
                        </div>
                        <div className="send-invoice-modal__summary-row">
                            <span className="send-invoice-modal__summary-label">Unit Price</span>
                            <span className="send-invoice-modal__summary-value">
                                {unitPrice > 0
                                    ? `${formatCurrency(unitPrice)} / ${weightUnit}`
                                    : "Not set on listing"}
                            </span>
                        </div>
                        <div className="send-invoice-modal__summary-row send-invoice-modal__summary-row--total">
                            <span className="send-invoice-modal__summary-label">Subtotal</span>
                            <span className="send-invoice-modal__summary-value">
                                {formatCurrency(lineTotal)}
                            </span>
                        </div>
                    </div>

                    {/* Tax & Notes */}
                    <div className="send-invoice-modal__extras">
                        <div className="send-invoice-modal__field">
                            <label className="send-invoice-modal__label">Tax Amount (optional)</label>
                            <div className="send-invoice-modal__price-wrap">
                                <span className="send-invoice-modal__price-prefix">$</span>
                                <input
                                    className="send-invoice-modal__input send-invoice-modal__input--price"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={taxAmount}
                                    onChange={(e) => setTaxAmount(e.target.value)}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                        <div className="send-invoice-modal__field">
                            <label className="send-invoice-modal__label">Notes (optional)</label>
                            <textarea
                                className="send-invoice-modal__textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Payment terms, special instructions..."
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Total */}
                    <div className="send-invoice-modal__preview">
                        {tax > 0 && (
                            <div className="send-invoice-modal__preview-row">
                                <span>Tax</span>
                                <span>{formatCurrency(tax)}</span>
                            </div>
                        )}
                        <div className="send-invoice-modal__preview-row send-invoice-modal__preview-row--total">
                            <span>Total</span>
                            <span>{formatCurrency(total)}</span>
                        </div>
                    </div>
                </div>

                <div className="send-invoice-modal__footer">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={onCancel}
                        disabled={isSending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSend}
                        disabled={isSending}
                        loading={isSending}
                    >
                        Send Invoice
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SendInvoiceModal;
